/**
 * Internal remap: re-run labelmap preprocessing with new clustering params,
 * then apply safe-reset UUID carry-over rules (plan A3 "Internal remap").
 *
 * Safe-reset rule: old UUIDs are preserved only when:
 *   - The new palette entry has an exact-RGB match with the old entry, AND
 *   - Spatial overlap (Jaccard index) between the old and new pixel sets >= 0.5.
 *
 * All other entries get fresh UUIDs and default names; overrides are dropped.
 *
 * Pure portable module - no DOM globals beyond ImageData constructor.
 * Depends on: labelmap-import (parseLabelmapImage, deriveLabelPlane),
 *             labelmap-preprocess (preprocessLabelmap, ClusteringParams),
 *             uuid (generateUuid).
 */

import type { DemoState, RegionConfig, RegionEntry, StatePatch } from '../state';
import type { ClusteringParams } from './labelmap-preprocess';
import { preprocessLabelmap } from './labelmap-preprocess';
import { parseLabelmapImage } from './labelmap-import';
import { generateUuid } from './uuid';

// ─── Helper: derive label-plane from parsed labelMap ──────────────────────────

function deriveLabelPlane(labelMapData: ImageData): Uint8Array {
  const total = labelMapData.width * labelMapData.height;
  const plane = new Uint8Array(total);
  for (let i = 0; i < total; i++) {
    plane[i] = labelMapData.data[i * 4]!;
  }
  return plane;
}

// ─── Helper: compute Jaccard(A, B) using pixel counts ─────────────────────────

/**
 * Compute the Jaccard index (intersection / union) between two pixel sets
 * represented as sets of pixel indices.
 *
 * For efficiency the sets are stored as typed arrays sorted ascending so we can
 * do a merge-pass in O(n+m). For typical region sizes (tens of thousands of
 * pixels) this stays fast on the main thread.
 */
function jaccardIndex(a: Set<number>, b: Set<number>): number {
  if (a.size === 0 && b.size === 0) return 1;
  if (a.size === 0 || b.size === 0) return 0;

  let intersection = 0;
  // Iterate the smaller set and check membership in the larger.
  const [smaller, larger] = a.size <= b.size ? [a, b] : [b, a];
  for (const idx of smaller) {
    if (larger.has(idx)) intersection++;
  }
  const union = a.size + b.size - intersection;
  return union === 0 ? 1 : intersection / union;
}

// ─── Public entry point ───────────────────────────────────────────────────────

/**
 * Re-run preprocessing on the original labelmap image with updated clustering
 * params, re-parse the result, and apply safe-reset UUID carry-over.
 *
 * Returns a StatePatch ready for `store.patch(...)`. Returns an empty patch if
 * `state.originalLabelmapImage` is null (no labelmap loaded).
 *
 * The returned patch includes:
 *   - labelMap, regions, regionEntries, regionLabelPlane (new values)
 *   - activeRegionUuids (preserved where UUIDs survived, else first entry)
 *   - labelmapError: null (success path)
 *   - remapMaxK, remapDeltaE (echoed back so state stays consistent)
 *
 * Plan A3 internal-remap safe-reset logic:
 *   1. Exact-RGB match between old and new palettes.
 *   2. Jaccard(old pixel set, new pixel set) >= 0.5.
 *   Both conditions required. No LAB fallback (safe-reset is strict).
 */
export function remapLabelmap(
  state: DemoState,
  params: ClusteringParams,
  nameForLabel?: (label: number) => string,
): StatePatch {
  if (state.originalLabelmapImage === null) {
    // No original image stored - cannot remap.
    return {};
  }

  const src = state.originalLabelmapImage;

  // ── Step 1: Preprocess with new params ────────────────────────────────────
  const preprocessResult = preprocessLabelmap(src, params);
  const reducedImage = preprocessResult.output;

  // ── Step 2: Parse the reduced image ──────────────────────────────────────
  let parsed;
  try {
    parsed = parseLabelmapImage(reducedImage);
  } catch {
    // If parsing fails (e.g. still > 64 colours after preprocessing), bail out.
    return {};
  }

  if (parsed.palette.length === 0) {
    // Empty result - nothing to remap.
    return {};
  }

  // ── Step 3: Build new label plane from parsed result ─────────────────────
  const newLabelPlane = deriveLabelPlane(parsed.labelMap);
  const total = newLabelPlane.length;

  // ── Step 4: Build pixel sets per label for the NEW parse ─────────────────
  // Map: label index (1-based) → Set of pixel indices
  const newPixelSets = new Map<number, Set<number>>();
  for (const entry of parsed.palette) {
    newPixelSets.set(entry.label, new Set());
  }
  for (let i = 0; i < total; i++) {
    const label = newLabelPlane[i]!;
    if (label !== 0) {
      newPixelSets.get(label)!.add(i);
    }
  }

  // ── Step 5: Build pixel sets per UUID for the OLD region entries ──────────
  const oldLabelPlane = state.regionLabelPlane;
  // Map: old entry uuid → { rgb packed, pixelSet }
  const oldEntryMap = new Map<
    string,
    { rgb: [number, number, number]; pixelSet: Set<number> }
  >();
  // Map: packed old RGB → uuid (for exact-RGB match lookup)
  const oldRgbToUuid = new Map<number, string>();

  for (const entry of state.regionEntries) {
    const packed = (entry.rgb[0] << 16) | (entry.rgb[1] << 8) | entry.rgb[2];
    oldRgbToUuid.set(packed, entry.uuid);
    oldEntryMap.set(entry.uuid, {
      rgb: entry.rgb,
      pixelSet: new Set(),
    });
  }

  // Populate old pixel sets from the stored label plane.
  // The old label plane uses label indices - we map through regionEntries.
  const oldLabelToUuid = new Map<number, string>();
  for (const entry of state.regionEntries) {
    oldLabelToUuid.set(entry.label, entry.uuid);
  }

  if (oldLabelPlane.length === total) {
    for (let i = 0; i < total; i++) {
      const oldLabel = oldLabelPlane[i]!;
      if (oldLabel !== 0) {
        const uuid = oldLabelToUuid.get(oldLabel);
        if (uuid !== undefined) {
          oldEntryMap.get(uuid)!.pixelSet.add(i);
        }
      }
    }
  }

  // ── Step 6: Build new regionEntries with safe-reset carry-over ───────────
  const newRegionEntries: RegionEntry[] = [];

  for (const paletteEntry of parsed.palette) {
    const newRgbPacked =
      (paletteEntry.rgb[0] << 16) |
      (paletteEntry.rgb[1] << 8) |
      paletteEntry.rgb[2];

    // Attempt exact-RGB match.
    const matchedUuid = oldRgbToUuid.get(newRgbPacked);
    const oldInfo = matchedUuid !== undefined ? oldEntryMap.get(matchedUuid) : undefined;
    const newPixelSet = newPixelSets.get(paletteEntry.label)!;

    let carriedEntry: RegionEntry | null = null;

    if (matchedUuid !== undefined && oldInfo !== undefined) {
      // Check Jaccard >= 0.5.
      const jaccard = jaccardIndex(oldInfo.pixelSet, newPixelSet);
      if (jaccard >= 0.5) {
        // Find the old entry to carry over userName, override, stackId.
        const oldEntry = state.regionEntries.find((e) => e.uuid === matchedUuid);
        if (oldEntry) {
          carriedEntry = {
            uuid: oldEntry.uuid,
            label: paletteEntry.label,
            rgb: paletteEntry.rgb,
            userName: oldEntry.userName,
            override: oldEntry.override,
            stackId: oldEntry.stackId,
          };
        }
      }
    }

    if (carriedEntry !== null) {
      newRegionEntries.push(carriedEntry);
    } else {
      // Safe-reset: fresh UUID, default name, no override.
      // Use the provided nameForLabel callback when available so that
      // locale-aware callers (e.g. controls.ts) can produce translated names
      // instead of hard-coded English "Area N".
      newRegionEntries.push({
        uuid: generateUuid(),
        label: paletteEntry.label,
        rgb: paletteEntry.rgb,
        userName: nameForLabel
          ? nameForLabel(paletteEntry.label)
          : `Area ${paletteEntry.label}`,
        override: null,
        stackId: null,
      });
    }
  }

  // ── Step 7: Derive RegionConfig[] for legacy pipeline compatibility ────────
  const newRegions: RegionConfig[] = parsed.palette.map((entry) => ({
    label: entry.label,
    rgb: entry.rgb,
    override: null,
  }));

  // ── Step 8: Compute activeRegionUuids ─────────────────────────────────────
  // Preserve previously active UUIDs if they survived; else fall back to first.
  const survivingUuidSet = new Set(newRegionEntries.map((e) => e.uuid));
  const preservedActive = state.activeRegionUuids.filter((id) =>
    survivingUuidSet.has(id),
  );
  const activeRegionUuids =
    preservedActive.length > 0
      ? preservedActive
      : newRegionEntries[0] !== undefined
      ? [newRegionEntries[0].uuid]
      : [];

  return {
    labelMap: parsed.labelMap,
    regions: newRegions,
    labelmapError: null,
    regionEntries: newRegionEntries,
    regionLabelPlane: newLabelPlane,
    activeRegionUuids,
    remapMaxK: params.maxK,
    remapDeltaE: params.deltaE,
  };
}

/**
 * The number of entries preserved (stable identity) in the most recent
 * remapLabelmap call. Extracted from the StatePatch for status display.
 *
 * Computes the count by comparing the new regionEntries against the old
 * activeRegionUuids or the full old set. Used by the controls status renderer.
 */
export function countPreservedEntries(
  oldEntries: readonly RegionEntry[],
  newEntries: readonly RegionEntry[],
): number {
  const oldUuids = new Set(oldEntries.map((e) => e.uuid));
  return newEntries.filter((e) => oldUuids.has(e.uuid)).length;
}
